import { useState } from "react";
import { useApolloClient, useMutation } from "@apollo/client";
import { intl } from "LocaleWrapper";

import { Table, Card } from "@cofano/cck";
import DeleteIcon from "@mui/icons-material/Delete";
import PublishIcon from "@mui/icons-material/Publish";

import { useConfirm } from "components/hooks/useConfirm";
import DocumentUploadDialog from "components/documents/DocumentUploadDialog";
import { TableId, useBackendTable } from "components/table";

import { AdminSupportManual } from "view/admin/utils/admin.types";

import { Permission, DocumentEntity } from "__generated__/enums";
import { GetAdminFaqManualsDocument } from "__generated__/view/admin/graphql/admin.queries.generated";
import { DeleteAdminFaqManualDocument } from "__generated__/view/admin/graphql/admin.mutations.generated";

type AdminSupportPdf = {
	id: number;
	description: string;
	manual: AdminSupportManual | undefined;
};

const AdminSupportPdf = () => {

	const confirm = useConfirm();
	const client = useApolloClient();

	const [ deleteAdminFaqManual ] = useMutation(DeleteAdminFaqManualDocument);

	const [ uploadDialog, setUploadDialog ] = useState<{ open: boolean, row: AdminSupportPdf | null }>({
		open: false,
		row: null,
	});

	const { tableProps, refetch, refetchAfterDelete } = useBackendTable({
		id: TableId.AdminFaqManuals,
		onChange: async(_pagination, _sort, _filter, callback) => {
			try {
				const result = await client.query({
					query: GetAdminFaqManualsDocument,
				});

				const manuals = result.data?.adminFaqManuals ?? [];
				const content: AdminSupportPdf[] = [ {
					id: 1,
					description: intl.formatMessage({ id: "ADMIN_FAQ_MANUAL_TYPE_ADMINISTRATOR" }),
					manual: manuals.find(manual => manual?.permission === Permission.Administrator),
				}, {
					id: 2,
					description: intl.formatMessage({ id: "ADMIN_FAQ_MANUAL_TYPE_PLANNER" }),
					manual: manuals.find(manual => manual?.permission === Permission.Main),
				}, {
					id: 4,
					description: intl.formatMessage({ id: "ADMIN_FAQ_MANUAL_TYPE_CUSTOMER" }),
					manual: manuals.find(manual => manual?.permission === Permission.Customer),
				}, {
					id: 5,
					description: intl.formatMessage({ id: "ADMIN_FAQ_MANUAL_TYPE_SKIPPER" }),
					manual: manuals.find(manual => manual?.permission === Permission.Skipper),
				}, {
					id: 6,
					description: intl.formatMessage({ id: "ADMIN_FAQ_MANUAL_TYPE_OPERATOR" }),
					manual: manuals.find(manual => manual?.permission === Permission.Operator),
				}, {
					id: 7,
					description: intl.formatMessage({ id: "ADMIN_FAQ_MANUAL_TYPE_TERMINAL" }),
					manual: manuals.find(manual => manual?.permission === Permission.Terminal),
				}, {
					id: 8,
					description: intl.formatMessage({ id: "ADMIN_FAQ_MANUAL_TYPE_MASTERDATA" }),
					manual: manuals.find(manual => manual?.permission === Permission.Masterdata),
				}, {
					id: 10,
					description: intl.formatMessage({ id: "ADMIN_FAQ_MANUAL_TYPE_FINANCE" }),
					manual: manuals.find(manual => manual?.permission === Permission.Finance),
				}, {
					id: 11,
					description: intl.formatMessage({ id: "ADMIN_FAQ_MANUAL_TYPE_MAIN_USER_LIMITED" }),
					manual: manuals.find(manual => manual?.permission === Permission.MainUserLimited),
				} ];

				callback({
					content,
					totalResults: content.length,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const deleteUpload = (id: number) => {
		deleteAdminFaqManual({
			variables: {
				id,
			},
		}).then(() => refetchAfterDelete());
	};

	return (
		<Card title={intl.formatMessage({ id: "ADMIN_FAQ_PDF_TITLE" })}>
			<Table
				{...tableProps}
				disableFooter
				columns={[ {
					id: "description",
					title: intl.formatMessage({ id: "USER_TYPE" }),
				}, {
					id: "document",
					title: intl.formatMessage({ id: "FILE" }),
					render: (row: AdminSupportPdf) => {
						return row.manual ? <a href={`/documents/download/${row.manual.document?.id}`} target="_blank">{row.manual.document?.filename}</a> : undefined;
					},
				} ]}
				rowActions={[ {
					icon: <PublishIcon />,
					onClick: (row: AdminSupportPdf) => setUploadDialog({ open: true, row: row }),
					disabled: (row: AdminSupportPdf) => Boolean(row.manual),
				}, {
					icon: <DeleteIcon />,
					disabled: (row: AdminSupportPdf) => !row.manual,
					onClick: (row: AdminSupportPdf) => {
						confirm({
							type: "DELETE",
						}).then(() => {
							if (row.manual) {
								deleteUpload(row.manual.id);
							}
						});
					},
				} ]} />


			{uploadDialog.open && uploadDialog.row && (
				<DocumentUploadDialog
					open={uploadDialog.open}
					onClose={() => setUploadDialog({ open: false, row: null })}
					entities={[ {
						id: uploadDialog.row.id,
						name: uploadDialog.row.description,
						type: DocumentEntity.Manual,
					} ]}
					onUploaded={() => refetch()} />
			)}
		</Card>
	);
};

export default AdminSupportPdf;
